import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  CheckCircle, 
  Send,
  Circle
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Question {
  id: number;
  questionText: string;
  type?: 'multiple_choice' | 'text' | 'long_text';
  questionType?: string;
  options?: string[];
}

interface VerticalQuizProps {
  questions: Question[];
  answers: { [key: number]: string };
  onAnswerChange: (questionId: number, answer: string) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  disabled?: boolean;
  className?: string;
  existingAnswers?: any[]; // Server-side answers that should be grayed out
}

export function VerticalQuiz({
  questions,
  answers,
  onAnswerChange,
  onSubmit,
  isSubmitting = false,
  disabled = false,
  className,
  existingAnswers = []
}: VerticalQuizProps) {
  // Create a map of existing answers from server for quick lookup
  const existingAnswerMap = new Map();
  if (existingAnswers && Array.isArray(existingAnswers)) {
    existingAnswers.forEach((answer: any) => {
      existingAnswerMap.set(answer.questionId, answer.answerText || answer.answer);
    });
  }

  // Count only new local answers that haven't already been submitted to server
  const localNewAnswerCount = Object.keys(answers).filter(questionId => 
    !existingAnswerMap.has(parseInt(questionId))
  ).length;
  
  // Count answers that have been changed from existing values
  const changedAnswerCount = Object.entries(answers).filter(([questionId, answer]) => {
    const existingAnswer = existingAnswerMap.get(parseInt(questionId));
    return existingAnswer !== undefined && existingAnswer !== answer;
  }).length;
  
  // Total count of answers that need submission (new + changed)
  const answersToSubmitCount = localNewAnswerCount + changedAnswerCount;
  
  const totalAnsweredQuestions = existingAnswerMap.size + localNewAnswerCount;
  const progressPercentage = questions.length > 0 ? Math.min((totalAnsweredQuestions / questions.length) * 100, 100) : 0;

  const handleSubmitQuiz = () => {
    // Allow submission if there are new answers (individual submissions)
    if (localNewAnswerCount > 0) {
      onSubmit();
    }
  };

  if (!questions || questions.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">No questions available</p>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const questionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.23, 1, 0.32, 1]
      }
    }
  };

  return (
    <motion.div
      className={cn("space-y-6", className)}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      data-testid="vertical-quiz-container"
    >
      {/* Progress Header */}
      <motion.div 
        className="sticky top-0 z-10 bg-white dark:bg-gray-900 p-4 pt-safe-top rounded-lg shadow-sm border"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="space-y-3">
          {/* Progress Bar */}
          <div className="flex justify-between items-center text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-300">
              Quiz Progress
            </span>
            <span className="text-gray-600 dark:text-gray-400">
              {totalAnsweredQuestions} of {questions.length} answered
            </span>
          </div>
          <Progress 
            value={progressPercentage} 
            className="h-3 bg-gray-200 dark:bg-gray-700"
            data-testid="quiz-progress"
          />
          
          {/* Progress Badge */}
          <div className="flex justify-center">
            <Badge 
              variant="outline" 
              className={cn(
                "px-4 py-2",
                progressPercentage === 100 
                  ? "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300"
                  : "bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300"
              )}
            >
              {progressPercentage === 100 ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  All Questions Completed
                </>
              ) : (
                <>
                  <Circle className="h-4 w-4 mr-2" />
                  {Math.round(progressPercentage)}% Complete
                </>
              )}
            </Badge>
          </div>
        </div>
      </motion.div>

      {/* Questions List */}
      <div className="space-y-6">
        {questions.map((question, index) => {
          const hasAnswer = answers[question.id];
          const currentAnswer = answers[question.id] || "";
          const existingAnswer = existingAnswerMap.get(question.id);
          const isAlreadyAnswered = !!existingAnswer;
          const displayAnswer = isAlreadyAnswered ? existingAnswer : currentAnswer;

          return (
            <motion.div
              key={question.id}
              variants={questionVariants}
              data-testid={`question-card-${question.id}`}
            >
              <Card
                className={cn(
                  "transition-all duration-300 border-2 rounded-2xl overflow-hidden",
                  isAlreadyAnswered
                    ? "border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800/50 opacity-70"
                    : hasAnswer
                    ? "border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20 shadow-lg ring-1 ring-green-100 dark:ring-green-900"
                    : "border-gray-200 dark:border-gray-800 hover:border-blue-200 dark:hover:border-blue-800 hover:shadow-lg hover:scale-[1.01]",
                  (disabled || isAlreadyAnswered) && "opacity-60"
                )}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      {/* Question Number */}
                      <div
                        className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-sm",
                          isAlreadyAnswered
                            ? "bg-gray-500 text-white"
                            : hasAnswer
                            ? "bg-green-600 text-white shadow-green-200 dark:shadow-green-900"
                            : "bg-blue-600 text-white shadow-blue-200 dark:shadow-blue-900"
                        )}
                        data-testid={`question-number-${index + 1}`}
                      >
                        {index + 1}
                      </div>
                      
                      {/* Question Type Badge */}
                      <Badge 
                        variant="secondary" 
                        className="text-xs px-3 py-1 rounded-full font-medium"
                        data-testid={`question-type-${question.questionType || question.type || 'unknown'}`}
                      >
                        {(question.questionType === 'mcq' || question.type === 'multiple_choice') ? 'MULTIPLE CHOICE' : 'TEXT QUESTION'}
                      </Badge>
                    </div>

                    {/* Answer Status */}
                    {(hasAnswer || isAlreadyAnswered) && (
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "px-3 py-1 rounded-full font-medium",
                          isAlreadyAnswered
                            ? "bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400"
                            : "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300"
                        )}
                        data-testid={`answer-status-${question.id}`}
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {isAlreadyAnswered ? "Previously Answered" : "Answered"}
                      </Badge>
                    )}
                  </div>
                  
                  <CardTitle className={cn(
                    "text-lg font-semibold leading-relaxed pt-2",
                    isAlreadyAnswered
                      ? "text-gray-500 dark:text-gray-400"
                      : "text-gray-900 dark:text-white"
                  )}>
                    {question.questionText}
                  </CardTitle>
                </CardHeader>

                <CardContent className="pt-0">
                  {/* Multiple Choice Options */}
                  {(question.questionType === 'mcq' || question.type === 'multiple_choice') && question.options && (
                    <RadioGroup
                      value={displayAnswer}
                      onValueChange={(value) => !isAlreadyAnswered && onAnswerChange(question.id, value)}
                      disabled={disabled || isAlreadyAnswered}
                      className="space-y-2"
                      data-testid={`radio-group-${question.id}`}
                    >
                      {question.options.map((option, optionIndex) => (
                        <motion.label
                          key={optionIndex}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: Math.min(index * 0.05 + optionIndex * 0.02, 0.3) }}
                          htmlFor={`q${question.id}_option${optionIndex}`}
                          className={cn(
                            "flex items-center space-x-3 p-3 rounded-xl transition-all duration-200",
                            "border-2 border-gray-200 dark:border-gray-700",
                            "focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2",
                            isAlreadyAnswered 
                              ? "cursor-not-allowed opacity-60" 
                              : "cursor-pointer active:scale-[0.98] hover:bg-blue-50 dark:hover:bg-blue-950/30 hover:border-blue-300 dark:hover:border-blue-700",
                            displayAnswer === option && (isAlreadyAnswered 
                              ? "bg-gray-200 dark:bg-gray-700 border-gray-400 dark:border-gray-500" 
                              : "bg-blue-100 dark:bg-blue-950/40 border-blue-400 dark:border-blue-600 shadow-md ring-1 ring-blue-200 dark:ring-blue-800"
                            )
                          )}
                          onClick={() => !disabled && !isAlreadyAnswered && onAnswerChange(question.id, option)}
                        >
                          <RadioGroupItem
                            value={option}
                            id={`q${question.id}_option${optionIndex}`}
                            className={cn(
                              "w-5 h-5 border-2 flex-shrink-0 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                              displayAnswer === option
                                ? "border-blue-600 text-blue-600"
                                : "border-gray-400 dark:border-gray-500"
                            )}
                            data-testid={`radio-option-${question.id}-${optionIndex}`}
                            disabled={disabled || isAlreadyAnswered}
                          />
                          <span
                            className={cn(
                              "text-base leading-relaxed font-medium select-none flex-1",
                              isAlreadyAnswered 
                                ? "text-gray-500 dark:text-gray-400" 
                                : "text-gray-900 dark:text-white"
                            )}
                          >
                            {option}
                          </span>
                        </motion.label>
                      ))}
                    </RadioGroup>
                  )}

                  {/* Text Input */}
                  {(question.questionType === 'text' || question.type === 'text') && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="space-y-2"
                    >
                      <Label 
                        htmlFor={`text-input-${question.id}`}
                        className="text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Your Answer:
                      </Label>
                      <Input
                        id={`text-input-${question.id}`}
                        value={displayAnswer}
                        onChange={(e) => !isAlreadyAnswered && onAnswerChange(question.id, e.target.value)}
                        placeholder={isAlreadyAnswered ? "Already answered" : "Type your answer here..."}
                        disabled={disabled || isAlreadyAnswered}
                        inputMode="text"
                        enterKeyHint="next"
                        className={cn(
                          "w-full h-14 text-base border-2 focus:border-blue-500 dark:focus:border-blue-400 rounded-xl",
                          "focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200",
                          isAlreadyAnswered 
                            ? "border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                            : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                        )}
                        data-testid={`input-text-${question.id}`}
                      />
                    </motion.div>
                  )}

                  {/* Long Text Input */}
                  {(question.questionType === 'long_text' || question.type === 'long_text') && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="space-y-2"
                    >
                      <Label 
                        htmlFor={`longtext-input-${question.id}`}
                        className="text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Your Answer:
                      </Label>
                      <Textarea
                        id={`longtext-input-${question.id}`}
                        value={displayAnswer}
                        onChange={(e) => !isAlreadyAnswered && onAnswerChange(question.id, e.target.value)}
                        placeholder={isAlreadyAnswered ? "Already answered" : "Type your detailed answer here..."}
                        disabled={disabled || isAlreadyAnswered}
                        inputMode="text"
                        enterKeyHint="done"
                        className={cn(
                          "w-full min-h-[120px] text-base border-2 focus:border-blue-500 dark:focus:border-blue-400 resize-y rounded-xl",
                          "focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200",
                          isAlreadyAnswered 
                            ? "border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                            : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                        )}
                        data-testid={`textarea-longtext-${question.id}`}
                      />
                    </motion.div>
                  )}

                  {/* Current Answer Preview */}
                  {(hasAnswer || isAlreadyAnswered) && displayAnswer && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className={cn(
                        "mt-4 p-3 rounded-lg border",
                        isAlreadyAnswered
                          ? "bg-gray-50 dark:bg-gray-800/30 border-gray-200 dark:border-gray-700"
                          : "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800"
                      )}
                    >
                      <p className={cn(
                        "text-xs font-medium mb-1",
                        isAlreadyAnswered
                          ? "text-gray-600 dark:text-gray-400"
                          : "text-green-800 dark:text-green-200"
                      )}>
                        {isAlreadyAnswered ? "Previously Submitted Answer:" : "Your Answer:"}
                      </p>
                      <p className={cn(
                        "text-sm",
                        isAlreadyAnswered
                          ? "text-gray-700 dark:text-gray-300"
                          : "text-green-700 dark:text-green-300"
                      )}>
                        {displayAnswer.length > 100 ? `${displayAnswer.substring(0, 100)}...` : displayAnswer}
                      </p>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Submit Section */}
      <motion.div
        className="sticky bottom-0 bg-white dark:bg-gray-900 p-6 pb-safe-bottom rounded-lg shadow-lg border border-gray-200 dark:border-gray-800"
        style={{ paddingBottom: 'max(1.5rem, calc(1.5rem + env(safe-area-inset-bottom)))' }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <div className="space-y-4">
          {/* Submit Button */}
          <Button
            onClick={handleSubmitQuiz}
            disabled={isSubmitting || answersToSubmitCount === 0 || disabled}
            size="lg"
            className={cn(
              "w-full flex items-center justify-center space-x-2 h-16 text-lg font-semibold rounded-2xl",
              "transition-all duration-200 active:scale-[0.98] disabled:active:scale-100",
              "shadow-lg focus:ring-4 focus:ring-offset-2",
              totalAnsweredQuestions === questions.length
                ? "bg-green-600 hover:bg-green-700 text-white shadow-green-200 dark:shadow-green-900 focus:ring-green-500"
                : "bg-blue-600 hover:bg-blue-700 text-white shadow-blue-200 dark:shadow-blue-900 focus:ring-blue-500"
            )}
            data-testid="button-submit-quiz"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <Send className="h-5 w-5" />
                <span>
                  {localNewAnswerCount === 0 
                    ? "No New Answers to Submit"
                    : `Submit ${localNewAnswerCount} Answer${localNewAnswerCount === 1 ? '' : 's'} (${localNewAnswerCount} ${localNewAnswerCount === 1 ? 'Entry' : 'Entries'})`
                  }
                </span>
              </>
            )}
          </Button>

          {/* Submit Status */}
          {localNewAnswerCount === 0 && totalAnsweredQuestions < questions.length && (
            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              Answer questions to earn entries (1 entry per answer)
            </p>
          )}
          {localNewAnswerCount === 0 && totalAnsweredQuestions === questions.length && (
            <p className="text-center text-sm text-green-600 dark:text-green-400 font-medium">
              ✓ All questions answered - You earned {totalAnsweredQuestions} entries!
            </p>
          )}
          {localNewAnswerCount > 0 && (
            <p className="text-center text-sm text-blue-600 dark:text-blue-400">
              Ready to earn {localNewAnswerCount} more {localNewAnswerCount === 1 ? 'entry' : 'entries'}!
            </p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}